import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Clock, AlertCircle } from 'lucide-react';

const STORAGE_KEY = 'quizProgress';
const BANNER_DISMISSED_KEY = 'quizRetargetingDismissed';
const DISMISS_HOURS = 24; // Show again after 24 hours if dismissed

const QuizRetargetingBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(null);
  const [resumeUrl, setResumeUrl] = useState('/sleep-apnea-quiz');

  useEffect(() => {
    // Don't show on the quiz page itself
    if (window.location.pathname === '/sleep-apnea-quiz') return;

    // Check if banner was recently dismissed
    const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissedAt) {
      const hoursSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSince < DISMISS_HOURS) return;
    }

    // Check for incomplete quiz progress
    const savedProgress = localStorage.getItem(STORAGE_KEY);
    if (savedProgress) {
      try {
        const data = JSON.parse(savedProgress);
        const hoursSinceQuiz = (Date.now() - data.timestamp) / (1000 * 60 * 60);
        
        // Only show if quiz was started within the last 7 days
        if (hoursSinceQuiz < 168 && !data.completed) {
          const answeredCount = Object.keys(data.answers).length;
          const percentComplete = Math.round((answeredCount / 10) * 100);
          
          setProgress({
            answeredCount,
            percentComplete,
            currentQuestion: data.currentQuestion,
            hoursSince: Math.round(hoursSinceQuiz)
          });

          // Generate resume URL with encoded progress
          const encoded = btoa(JSON.stringify({
            answers: data.answers,
            currentQuestion: data.currentQuestion
          }));
          setResumeUrl(`/sleep-apnea-quiz?resume=${encoded}`);
          
          // Delay showing banner slightly for better UX
          setTimeout(() => setIsVisible(true), 1500);

          // Track banner impression
          if (window.gtag) {
            window.gtag('event', 'retargeting_banner_shown', {
              'event_category': 'Sleep Apnea Quiz',
              'event_label': `${percentComplete}% Complete`,
              'value': answeredCount
            });
          }
        }
      } catch (e) {
        console.error('Failed to parse quiz progress');
      }
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
    
    // Track dismissal
    if (window.gtag) {
      window.gtag('event', 'retargeting_banner_dismissed', {
        'event_category': 'Sleep Apnea Quiz',
        'event_label': `${progress?.percentComplete}% Complete`
      });
    }
  };

  const handleClick = () => {
    // Track click
    if (window.gtag) {
      window.gtag('event', 'retargeting_banner_clicked', {
        'event_category': 'Sleep Apnea Quiz',
        'event_label': `${progress?.percentComplete}% Complete`
      });
    }
  };

  if (!isVisible || !progress) return null;

  // Personalized messaging based on progress
  const getMessage = () => {
    if (progress.percentComplete >= 70) {
      return "You're so close! Just a few more questions to get your results.";
    } else if (progress.percentComplete >= 40) {
      return "You're halfway there! Complete your assessment in under 2 minutes.";
    } else {
      return "You started our Sleep Apnea Risk Quiz. Pick up where you left off!";
    }
  };

  const getTimeAgo = () => {
    if (progress.hoursSince < 1) return 'just now';
    if (progress.hoursSince < 24) return `${progress.hoursSince} hour${progress.hoursSince > 1 ? 's' : ''} ago`;
    const days = Math.round(progress.hoursSince / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40 p-4 md:p-0 md:bottom-6 md:left-6 md:right-auto md:max-w-sm animate-in slide-in-from-bottom duration-500"
      data-testid="quiz-retargeting-banner"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-500"
            style={{ width: `${progress.percentComplete}%` }}
          />
        </div>
        
        <div className="p-4">
          {/* Header with dismiss */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center shrink-0">
                <AlertCircle size={20} className="text-cyan-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Continue Your Quiz</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock size={12} />
                  Started {getTimeAgo()}
                </p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              data-testid="retargeting-dismiss"
            >
              <X size={18} />
            </button>
          </div>

          {/* Message */}
          <p className="text-gray-600 text-sm mb-4">
            {getMessage()}
          </p>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl font-bold text-cyan-600">{progress.percentComplete}%</span>
            <span className="text-gray-500 text-sm">complete ({progress.answeredCount}/10 questions)</span>
          </div>

          {/* CTA */}
          <Link
            to={resumeUrl}
            onClick={handleClick}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid="retargeting-resume"
          >
            Resume My Quiz
            <ArrowRight size={18} />
          </Link>

          {/* Start fresh option */}
          <Link
            to="/sleep-apnea-quiz"
            onClick={handleDismiss}
            className="block mt-2 text-center text-gray-400 hover:text-gray-600 text-xs transition-colors"
          >
            Or start fresh
          </Link>
        </div>
      </div>
    </div>
  );
};

export default QuizRetargetingBanner;
